// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useParams } from 'react-router-dom';
import useUploadApi from 'hooks/useUploadApi';
import { changeSelectedImage } from 'store/reducers/projectImageSlice';
import useProjectLabels from 'hooks/useProjectLabels';
import GetOS, { OS } from 'components/common/GetOs';
import { exportMask, redoMask, resetMask, setMask, undoMask } from '../canvas/DrawHandlerExtension';
import { ImageListOrderContext } from '../images/ImageListOrderContext';
import { ImagesContext } from '../../images/ImagesContext';
import { clearEvaluationProgressCache } from 'store/reducers/projectEvaluationSlice';
import useAppPermissions from 'hooks/useAppPermissions';
import { LabelMetaDataContext } from '../LabelMetaDataProvider';
import ProcessTagPicker from './ProcessTagPicker';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import { UseCase } from '@tls/sw91-communication/types/com.base';
import { queueToast } from 'components/treactui-template/organisms/toast/ToastMessage';
import { Footer, FooterButton } from '@tls/treact-ui';
import { IconLoading, IconUndo } from '@tls/treact-ui/icons';
import useGlobalKeyboardEvent from 'hooks/useGlobalKeyboardEvent';

export default function ImageFooter() {
  const { t } = useTranslation();
  const { id } = useParams();

  const { selectedProject } = useProjectsMetadata(id);

  const { labels = [], selectedMd5 } = useProjectLabels();
  const dispatch = useAppDispatch();
  const artifactDetection = useAppSelector(s => s.tools.artifactDetection);
  const { hasPermissionForProject } = useAppPermissions();

  const [loading, setLoading] = useState<'cancel' | 'save'>();
  const resetAbortControllerRef = useRef<AbortController | undefined>(undefined);

  const { uploadMask } = useUploadApi(id ?? '');

  const reset = useCallback(async () => {
    if (!!loading || !id || !selectedMd5) return;

    const imageId = selectedMd5;
    const controller = new AbortController();
    resetAbortControllerRef.current?.abort();
    resetAbortControllerRef.current = controller;
    setLoading('cancel');
    try {
      const hasMask = labels.find(label => label.imageMd5 === imageId)?.hasMask;
      if (hasMask) {
        await setMask(`projects/${id}/labels/${imageId}/mask`, { signal: controller.signal, imageId });
      } else {
        resetMask();
        document.dispatchEvent(new CustomEvent('clearstack', { bubbles: true }));
      }
    } finally {
      if (resetAbortControllerRef.current === controller) {
        resetAbortControllerRef.current = undefined;
        setLoading(undefined);
      }
    }
  }, [id, labels, loading, selectedMd5]);

  useEffect(() => {
    resetAbortControllerRef.current?.abort();
  }, [selectedMd5]);

  useEffect(
    () => () => {
      resetAbortControllerRef.current?.abort();
      resetAbortControllerRef.current = undefined;
    },
    []
  );

  const { currentOrder } = useContext(ImageListOrderContext);

  const { editImage, updateMaskMetadata } = useContext(ImagesContext);

  const { isValidationDataset, qcResult } = useContext(LabelMetaDataContext);

  const showQCResultPicker = useMemo(() => {
    return selectedProject?.projectUseCase?.useCase === UseCase.QUALITY_CHECK;
  }, [selectedProject?.projectUseCase?.useCase]);

  const save = useCallback(async () => {
    if (!!loading || !selectedMd5 || !hasPermissionForProject || !id) return;

    dispatch(clearEvaluationProgressCache());
    setLoading('cancel');

    const file = await exportMask();
    if (!file) {
      setLoading(undefined);
      return;
    }

    const result = await uploadMask(file, selectedMd5 ?? '', isValidationDataset, qcResult);
    if (result?.status === 200) {
      updateMaskMetadata(selectedMd5, isValidationDataset, qcResult);
      editImage({ imageMd5: selectedMd5, hasMask: true });
    }

    if (!labels) {
      setLoading(undefined);
      return;
    }

    const timeout = setTimeout(() => {
      handleImageSave(new CustomEvent('saveImage', { bubbles: true, detail: { canSave: true, error: 'Timeout' } }));
    }, 5000);

    const handleImageSave = e => {
      if (e.detail.canSave) {
        const idx = currentOrder?.current.findIndex(l => l === selectedMd5);
        if (idx !== undefined && idx + 1 < (currentOrder?.current.length ?? 0)) {
          dispatch(changeSelectedImage(currentOrder?.current[idx + 1]));
        }
        setLoading('cancel');
      } else {
        queueToast({
          flag: 'warning',
          headline: t('project.label.tools.settings.detection'),
          message: t('project.label.tools.artifactDetection.detected'),
        });
      }
      if (e.detail.error) {
        queueToast({
          flag: 'warning',
          headline: t('project.label.tools.settings.detection'),
          message: t('project.label.tools.artifactDetection.failed'),
        });
      }
      setLoading(undefined);
      document.removeEventListener('saveImage', handleImageSave);
      clearTimeout(timeout);
    };

    document.addEventListener('saveImage', handleImageSave);

    document.dispatchEvent(
      new CustomEvent('detectarifacts', { bubbles: true, detail: { detectArtifact: artifactDetection } })
    );
  }, [
    artifactDetection,
    currentOrder,
    dispatch,
    labels,
    loading,
    selectedMd5,
    uploadMask,
    editImage,
    hasPermissionForProject,
    id,
    isValidationDataset,
    updateMaskMetadata,
    qcResult,
    t,
  ]);

  const handleButtonPress = useCallback(
    (event: KeyboardEvent) => {
      const { key, metaKey, ctrlKey } = event;

      const os = GetOS();
      const IsCtrlPressed = os === OS.MacOs ? metaKey : ctrlKey;
      if (key.toLowerCase() === 's' && IsCtrlPressed) {
        save();
        event.preventDefault();
      } else if (key.toLowerCase() === 'r' && IsCtrlPressed) {
        reset();
        event.preventDefault();
      }
    },
    [reset, save]
  );

  useGlobalKeyboardEvent('keydown', handleButtonPress);

  return (
    <Footer
      left={[
        <FooterButton
          key='undo'
          icon={<IconUndo />}
          variant='secondary'
          onClick={undoMask}
          data-testid='label-footer-undo-button'
        />,
        <FooterButton
          key='redo'
          icon={<IconUndo className='mirror' />}
          variant='secondary'
          onClick={redoMask}
          data-testid='label-footer-redo-button'
        />,
      ]}
      right={[
        <FooterButton
          key='cancel'
          label={t('cancel')}
          variant='secondary'
          icon={loading === 'cancel' ? <IconLoading /> : undefined}
          onClick={reset}
          disabled={!!loading || !selectedMd5}
          data-testid='label-footer-reset-button'
        />,
        showQCResultPicker && <ProcessTagPicker key='processTagPicker' disabled={!!loading || !selectedMd5} />,
        <FooterButton
          key='redo'
          icon={loading === 'save' ? <IconLoading /> : undefined}
          label={t('saveAndNext')}
          onClick={save}
          disabled={!!loading || !selectedMd5 || !hasPermissionForProject}
          data-testid='label-footer-save-next-button'
        />,
      ]}
    />
  );
}
